/**
 * Collection Filtering, Sorting, and Infinite Scroll
 *
 * Fetches the next pages only when the visitor gets close, with one page as a
 * buffer. Before this, loading any collection page fetched EVERYTHING right
 * away: on /collections/alle-kamerplanten that was 22 pages of 671 KB, together
 * almost 15 MB of HTML that was also parsed one by one as a full document. At
 * 1,358 ms that was the heaviest script on the page.
 *
 * Two changes:
 *  - lazy loading instead of everything up front, with a buffer of one page so
 *    the first scroll still responds immediately;
 *  - fetching through ?section_id=, so only the collection section comes back
 *    (313 KB instead of 671 KB) and no complete document gets parsed.
 */
document.addEventListener("DOMContentLoaded", function () {
  const filterForm = document.querySelector("#filters");
  const sortSelects = document.querySelectorAll(".js-sort-by");
  const productGrid = document.querySelector("#products-grid");
  const infiniteScrollTrigger = document.querySelector("#infinite-scroll-trigger");
  const loadingIndicator = document.querySelector("#infinite-scroll-loading");
  const endMessage = document.querySelector("#infinite-scroll-end");

  // Exit early if required elements are not found
  if (!filterForm || sortSelects.length === 0 || !productGrid) {
    return;
  }

  // Initialize Shopify query params object if not exists
  if (typeof Shopify === "undefined") {
    window.Shopify = {};
  }

  Shopify.queryParams = {};

  /**
   * GET with the same result as axios.get: { data }, parsed JSON when the server
   * sends JSON, text otherwise, and an error for a status outside 2xx.
   *
   * This page loaded axios separately from cdn.jsdelivr.net, without a version.
   * That was an extra connection to a foreign origin, 14 KB of script, and
   * possibly a different version every day, for something fetch already does.
   */
  const httpGet = async (url) => {
    const response = await fetch(url, { credentials: "same-origin" });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status} voor ${url}`);
    }

    const text = await response.text();
    const type = response.headers.get("content-type") || "";

    return { data: type.includes("json") ? JSON.parse(text) : text };
  };

  // Preload state
  let currentPage = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.currentPage) : 1;
  let totalPages = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.totalPages) : 1;
  let preloadedProducts = [];
  let isPreloading = false;
  let preloadComplete = false;
  let revealObserver = null;
  // The next page that still has to be fetched.
  let nextPage = currentPage + 1;

  // How many cards become visible at a time, and the lower bound below which
  // we fetch the next page ahead of time.
  const REVEAL_BATCH = 24;
  // How many cards have to be ready. On load one batch, so the page itself
  // does not have to wait for a second page of HTML. After the first scroll
  // further two: whoever scrolls faster than the network keeps up would
  // otherwise see the loading notice and the collection text below it in view
  // at the moment the new cards appear above them. That jump counts towards
  // CLS, because scrolling does not count as input.
  let bufferTarget = REVEAL_BATCH;
  // How far below the screen the trigger may be to already show the next
  // batch. Only below: nothing has to load above the screen. Wide enough that
  // the new cards are added out of view, also when scrolling fast.
  const REVEAL_MARGIN_PX = 1600;
  // The section that renders the product grid; with it Shopify returns only
  // that part instead of the whole page.
  const COLLECTION_SECTION_ID = "single-collection";

  /**
   * Get query parameters from the current URL
   */
  const getQueryParams = () => {
    const queryParams = {};

    if (location.search.length) {
      const params = new URLSearchParams(location.search);
      params.forEach((value, key) => {
        if (queryParams[key]) {
          queryParams[key] = [].concat(queryParams[key], value);
        } else {
          queryParams[key] = value;
        }
      });
    }

    return queryParams;
  };

  Shopify.queryParams = getQueryParams();

  /**
   * Build URL with current filters, sorting, and page number
   */
  const buildUrlWithParams = (pageNum) => {
    const queryParams = { ...Shopify.queryParams };
    queryParams.page = pageNum;

    const searchParams = new URLSearchParams();
    Object.keys(queryParams).forEach((key) => {
      if (Array.isArray(queryParams[key])) {
        queryParams[key].forEach((value) => searchParams.append(key, value));
      } else {
        searchParams.append(key, queryParams[key]);
      }
    });

    return `${window.location.pathname}?${searchParams.toString()}`;
  };

  /**
   * The loading notice keeps its height while more pages are coming and is only
   * made visible or invisible. With d-none the block under the grid grew a bit
   * on every fetch and shrank again: a shift of everything below it. Only when
   * nothing more comes does the space go away, in the same step in which the
   * last cards are added.
   *
   * The notice is only visible when the visitor is really waiting: the buffer
   * is empty and the next page is on its way. With background fetching it
   * otherwise stayed in view while cards from the buffer were inserted above
   * it, and then the notice itself visibly slid away. preloadNextPage hides it
   * again before the new cards are added.
   */
  const showLoader = (visible) => {
    if (loadingIndicator) loadingIndicator.classList.toggle("is-loading", visible);
  };

  const collapseLoader = () => {
    if (!loadingIndicator) return;
    loadingIndicator.classList.remove("is-loading");
    loadingIndicator.classList.add("d-none");
  };

  /**
   * Preload all remaining pages in the background
   */
  const preloadNextPage = async () => {
    if (isPreloading) return;

    if (nextPage > totalPages) {
      preloadComplete = true;
      showLoader(false);

      return;
    }

    isPreloading = true;

    try {
      const url = buildUrlWithParams(nextPage) + "&section_id=" + COLLECTION_SECTION_ID;
      const response = await httpGet(url);
      const parser = new DOMParser();
      const doc = parser.parseFromString(response.data, "text/html");

      const newProductsContainer = doc.querySelector("#products-grid");

      if (newProductsContainer) {
        const newProducts = newProductsContainer.querySelectorAll(":scope > div");
        newProducts.forEach((product) => {
          preloadedProducts.push(product.cloneNode(true));
        });
      }

      nextPage++;
    } catch (error) {
      console.error("Error preloading products:", error);
      // Count on after an error too, otherwise the same page keeps being
      // retried endlessly.
      nextPage++;
    }

    isPreloading = false;

    if (nextPage > totalPages) {
      preloadComplete = true;
    }

    showLoader(false);

    // When the trigger was already in view as this started, no new
    // intersection notification will come; then we show the cards here. This
    // stops by itself once there are enough cards to push the trigger out of
    // view.
    if (preloadedProducts.length > 0 && triggerIsNear()) {
      void revealMoreProducts();
    }

    // After the first scroll two batches are ready; top up to that.
    topUpBuffer();
  };

  /**
   * Still load the Trustpilot widgets in newly added cards.
   *
   * tp.widget.bootstrap.min.js scans the page once on load. Everything that
   * enters the DOM after that stays untouched: you then only see the fallback
   * text "Trustpilot" instead of the star bar. When scrolling through a
   * collection that happened from the second batch on.
   *
   * The live theme already behaved like this - it is not a consequence of the
   * switch to lazy loading, but it can now be solved neatly at the moment we
   * add cards ourselves.
   *
   * The same applies when sorting, filtering or a pill replaces the whole
   * grid: the trust banner in the grid then stayed as the bare word
   * "Trustpilot". Every place that replaces the grid therefore calls this
   * too.
   */
  const activeerTrustpilot = (elementen) => {
    // The loader in theme.liquid decides per widget: close and visible gets
    // drawn (and loads the bootstrap when needed), the rest waits. That way a
    // hidden variant also stays out of view when the bootstrap only loads AFTER
    // the grid was replaced and then draws everything with the class
    // trustpilot-widget.
    const loader =
      window.gbTrustpilot && window.gbTrustpilot.aanmelden instanceof Function
        ? window.gbTrustpilot
        : null;

    if (!loader && (!window.Trustpilot || !(window.Trustpilot.loadFromElement instanceof Function)))
      return;

    elementen.forEach((el) => {
      if (!el || !(el.querySelectorAll instanceof Function)) return;

      const widgets =
        el.matches && el.matches(".trustpilot-widget")
          ? [el]
          : Array.from(el.querySelectorAll(".trustpilot-widget"));

      widgets.forEach((widget) => {
        // Already initialised? Then it has an iframe and we leave it alone.
        if (widget.querySelector("iframe")) return;

        if (loader) {
          loader.aanmelden(widget);

          return;
        }

        // Hidden variant (for example the desktop banner on mobile): it would
        // otherwise get an empty iframe of height 0 and a request for nothing.
        if (widget.getClientRects().length === 0) return;

        try {
          window.Trustpilot.loadFromElement(widget, true);
        } catch {
          // Failing to load a widget is annoying but must not break the row.
        }
      });
    });
  };

  /**
   * Is the scroll trigger now within (or just below) the screen?
   *
   * The IntersectionObserver only reports a *change*. After filtering or
   * sorting the trigger is already in view and so fires once — exactly while
   * the first page is still being fetched, so that single notification got
   * lost and nothing was ever added after that. That is why we check where the
   * trigger is ourselves after fetching.
   */
  const triggerIsNear = () => {
    if (!infiniteScrollTrigger) return false;
    const rect = infiniteScrollTrigger.getBoundingClientRect();

    return rect.top < window.innerHeight + REVEAL_MARGIN_PX;
  };

  /**
   * Makes sure cards are ready ahead of time (see bufferTarget), so the next
   * scroll does not have to wait for the network.
   */
  const topUpBuffer = () => {
    if (!preloadComplete && !isPreloading && preloadedProducts.length < bufferTarget) {
      void preloadNextPage();
    }
  };

  /**
   * Reveal next batch of preloaded products
   */
  const revealMoreProducts = async () => {
    // Buffer empty but there are pages left: fetch first, then show.
    if (preloadedProducts.length === 0 && !preloadComplete) {
      showLoader(true);
      await preloadNextPage();
    }

    const productsToAdd = preloadedProducts.splice(0, REVEAL_BATCH);

    if (productsToAdd.length === 0) {
      if (preloadComplete && preloadedProducts.length === 0) {
        if (endMessage) endMessage.classList.remove("d-none");
        collapseLoader();

        if (revealObserver) revealObserver.disconnect();
      }

      return;
    }

    productsToAdd.forEach((product) => {
      productGrid.appendChild(product);
    });

    activeerTrustpilot(productsToAdd);

    // Check if all done
    if (preloadComplete && preloadedProducts.length === 0) {
      if (endMessage) endMessage.classList.remove("d-none");
      collapseLoader();

      if (revealObserver) revealObserver.disconnect();
    } else {
      // The visitor keeps scrolling: from now on keep two batches ready.
      bufferTarget = 2 * REVEAL_BATCH;
      topUpBuffer();
    }
  };

  /**
   * Initialize scroll-based reveal
   */
  const initRevealOnScroll = () => {
    if (!infiniteScrollTrigger) return;

    if (totalPages <= 1) {
      if (endMessage) endMessage.classList.remove("d-none");

      return;
    }

    if (revealObserver) {
      revealObserver.disconnect();
    }

    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // revealMoreProducts fetches by itself when nothing is ready.
          // When cards are ready we show them right away, also while the
          // next page is still on its way. Only with an empty buffer do we
          // wait: then isPreloading starts no second fetch, and
          // preloadNextPage shows the cards itself once they are in.
          if (!entry.isIntersecting) return;

          if (preloadedProducts.length > 0 || !isPreloading) {
            void revealMoreProducts();
          } else {
            // The visitor is waiting for the page that is already on its way.
            showLoader(true);
          }
        });
      },
      {
        root: null,
        rootMargin: `0px 0px ${REVEAL_MARGIN_PX}px 0px`,
        threshold: 0,
      },
    );

    revealObserver.observe(infiniteScrollTrigger);
  };

  /**
   * Reset state and preload products (used when filtering/sorting)
   */
  const resetAndPreload = (doc) => {
    const newTrigger = doc.querySelector("#infinite-scroll-trigger");

    if (newTrigger) {
      currentPage = parseInt(newTrigger.dataset.currentPage);
      totalPages = parseInt(newTrigger.dataset.totalPages);
    }

    preloadedProducts = [];
    isPreloading = false;
    preloadComplete = false;
    // After filtering/sorting the pagination starts over.
    nextPage = currentPage + 1;
    bufferTarget = REVEAL_BATCH;

    // Only keep room for the loading notice when more pages are coming.
    if (totalPages > 1) {
      if (loadingIndicator) loadingIndicator.classList.remove("d-none");
      showLoader(false);
    } else {
      collapseLoader();
    }

    if (endMessage) {
      if (totalPages <= 1) {
        endMessage.classList.remove("d-none");
      } else {
        endMessage.classList.add("d-none");
      }
    }

    initRevealOnScroll();
    topUpBuffer();
  };

  /**
   * Fetch ALL product IDs from a collection using Shopify JSON API
   * /collections/{handle}/products.json returns up to 250 per page
   */
  const collectionIdCache = {};

  const fetchCollectionProductIds = async (collectionHandle) => {
    // Return cached result if available
    if (collectionIdCache[collectionHandle]) return collectionIdCache[collectionHandle];

    const ids = new Set();
    let page = 1;

    while (true) {
      const rootPath =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : "/";

      const response = await httpGet(
        `${rootPath}collections/${collectionHandle}/products.json?limit=250&page=${page}`,
      );

      const products = response.data.products;

      if (!products || products.length === 0) break;
      products.forEach((p) => ids.add(String(p.id)));

      if (products.length < 250) break;
      page++;
    }

    collectionIdCache[collectionHandle] = ids;

    return ids;
  };

  /**
   * Fetch all pages of a collection's HTML and return matching product cards
   * Fetches page 1 first to get totalPages, then remaining pages in parallel
   */
  const fetchFilteredCards = async (collectionPath, targetIds) => {
    const matchingCards = [];

    // Fetch page 1 to get totalPages
    const separator = collectionPath.includes("?") ? "&" : "?";
    const firstResponse = await httpGet(`${collectionPath}${separator}page=1`);
    const firstDoc = new DOMParser().parseFromString(firstResponse.data, "text/html");
    const firstGrid = firstDoc.querySelector("#products-grid");

    if (!firstGrid) return matchingCards;

    const trigger = firstDoc.querySelector("#infinite-scroll-trigger");
    const maxPages = trigger ? parseInt(trigger.dataset.totalPages) : 1;

    // Collect matches from page 1
    firstGrid.querySelectorAll(":scope > div").forEach((card) => {
      const block = card.querySelector("[data-product-id]");

      if (block && targetIds.has(block.getAttribute("data-product-id"))) {
        matchingCards.push(card.cloneNode(true));
      }
    });

    // Fetch remaining pages in parallel
    if (maxPages > 1) {
      const pagePromises = [];

      for (let p = 2; p <= maxPages; p++) {
        pagePromises.push(
          httpGet(`${collectionPath}${collectionPath.includes("?") ? "&" : "?"}page=${p}`),
        );
      }

      const responses = await Promise.all(pagePromises);
      responses.forEach((response) => {
        const doc = new DOMParser().parseFromString(response.data, "text/html");
        const grid = doc.querySelector("#products-grid");

        if (grid) {
          grid.querySelectorAll(":scope > div").forEach((card) => {
            const block = card.querySelector("[data-product-id]");

            if (block && targetIds.has(block.getAttribute("data-product-id"))) {
              matchingCards.push(card.cloneNode(true));
            }
          });
        }
      });
    }

    return matchingCards;
  };

  // Track active collection pill for intersection filtering
  let activeCollectionPill = null;

  /**
   * Number of products from the counter in the collection header of a fetched
   * page. null when the page has no counter; then we show no number either.
   */
  const readProductCount = (doc) => {
    const counter = doc.querySelector(".js-product-count-number");

    if (!counter) return null;
    const count = parseInt(counter.textContent.trim(), 10);

    return Number.isNaN(count) ? null : count;
  };

  /**
   * Update query parameters and reload filtered results.
   *
   * Returns a promise with the number of products found (or null), and a
   * rejected promise when fetching fails. That way the "Toepassen" (Apply)
   * button can wait until the products are really there.
   */
  const updateParamsAndFilter = (resetPage = true) => {
    const queryParams = getQueryParams();
    const formData = new FormData(filterForm);

    Array.from(filterForm.elements).forEach((element) => {
      if (element.name && queryParams[element.name]) {
        delete queryParams[element.name];
      }
    });

    formData.forEach((value, key) => {
      if (value) {
        if (queryParams[key]) {
          queryParams[key] = [].concat(queryParams[key], value);
        } else {
          queryParams[key] = value;
        }
      }
    });

    if (resetPage) {
      delete queryParams.page;
    }

    if (Shopify.queryParams.sort_by) {
      queryParams.sort_by = Shopify.queryParams.sort_by;
    }

    const searchParams = new URLSearchParams();
    Object.keys(queryParams).forEach((key) => {
      if (Array.isArray(queryParams[key])) {
        queryParams[key].forEach((value) => searchParams.append(key, value));
      } else {
        searchParams.append(key, queryParams[key]);
      }
    });

    const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

    // Only change the URL once the products are in. When fetching fails the
    // old list stays, and a URL with the new filters would show a different
    // list on reload or share than the one on screen.
    // Before resetAndPreload, because the infinite scroll reads Shopify.queryParams.
    const commitUrl = () => {
      window.history.pushState({}, "", urlWithParams);
      Shopify.queryParams = queryParams;
    };

    // If a collection pill is active, apply intersection filtering
    if (activeCollectionPill) {
      const filteredUrl = `${originalCollectionPath}?${searchParams.toString()}`;

      return fetchCollectionProductIds(activeCollectionPill).then(async (targetIds) => {
        const matchingCards = await fetchFilteredCards(filteredUrl, targetIds);
        commitUrl();

        productGrid.innerHTML = "";
        matchingCards.forEach((card) => productGrid.appendChild(card));
        activeerTrustpilot([productGrid]);

        const productCount = document.querySelector(".collection-product-count");

        if (productCount) {
          const count = matchingCards.length;
          const countNumbers = productCount.querySelectorAll(".js-product-count-number");

          if (countNumbers.length) {
            countNumbers.forEach((el) => (el.textContent = count));
          } else {
            productCount.textContent = count + (count === 1 ? " product" : " producten");
          }
        }

        if (revealObserver) revealObserver.disconnect();
        preloadedProducts = [];
        preloadComplete = true;
        isPreloading = false;

        if (endMessage) endMessage.classList.remove("d-none");

        if (loadingIndicator) loadingIndicator.classList.add("d-none");

        return matchingCards.length;
      });
    } else {
      return httpGet(urlWithParams).then((response) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.data, "text/html");
        commitUrl();

        productGrid.innerHTML = doc.querySelector("#products-grid").innerHTML;
        activeerTrustpilot([productGrid]);

        const resultsDiv = document.querySelector("#results");
        const newResultsDiv = doc.querySelector("#results");

        if (resultsDiv && newResultsDiv) {
          resultsDiv.innerHTML = newResultsDiv.innerHTML;
        }

        const resultsDivMobile = document.querySelector("#results-mobile");
        const newResultsDivMobile = doc.querySelector("#results-mobile");

        if (resultsDivMobile && newResultsDivMobile) {
          resultsDivMobile.innerHTML = newResultsDivMobile.innerHTML;
        }

        // Update product count in collection header
        const productCount = document.querySelector(".collection-product-count");
        const newProductCount = doc.querySelector(".collection-product-count");

        if (productCount && newProductCount) {
          productCount.innerHTML = newProductCount.innerHTML;
        }

        // Do NOT replace filter form content — filters are applied via "Apply" button
        // and should remain as-is so users can adjust and re-apply.

        resetAndPreload(doc);

        return readProductCount(doc);
      });
    }
  };

  /**
   * Handle sort change
   */
  const handleSortChange = (sortValue) => {
    Shopify.queryParams.sort_by = sortValue;

    const queryParams = getQueryParams();
    queryParams.sort_by = sortValue;
    delete queryParams.page;

    Shopify.queryParams = queryParams;

    const searchParams = new URLSearchParams();
    Object.keys(queryParams).forEach((key) => {
      const val = queryParams[key];

      if (Array.isArray(val)) {
        val.forEach((v) => searchParams.append(key, v));
      } else {
        searchParams.append(key, String(val));
      }
    });

    const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

    window.history.pushState({}, "", urlWithParams);

    if (activeCollectionPill) {
      fetchCollectionProductIds(activeCollectionPill)
        .then(async (targetIds) => {
          const matchingCards = await fetchFilteredCards(urlWithParams, targetIds);

          productGrid.innerHTML = "";
          matchingCards.forEach((card) => productGrid.appendChild(card));
          activeerTrustpilot([productGrid]);

          const productCount = document.querySelector(".collection-product-count");

          if (productCount) {
            const count = matchingCards.length;
            const countNumbers = productCount.querySelectorAll(".js-product-count-number");

            if (countNumbers.length) {
              countNumbers.forEach((el) => (el.textContent = count));
            } else {
              productCount.textContent = count + (count === 1 ? " product" : " producten");
            }
          }

          if (revealObserver) revealObserver.disconnect();
          preloadedProducts = [];
          preloadComplete = true;
          isPreloading = false;

          if (endMessage) endMessage.classList.remove("d-none");

          if (loadingIndicator) loadingIndicator.classList.add("d-none");
        })
        .catch((error) => console.error("Error loading sorted products:", error));
    } else {
      httpGet(urlWithParams)
        .then((response) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(response.data, "text/html");

          productGrid.innerHTML = doc.querySelector("#products-grid").innerHTML;
          activeerTrustpilot([productGrid]);

          const resultsDiv = document.querySelector("#results");
          const newResultsDiv = doc.querySelector("#results");

          if (resultsDiv && newResultsDiv) {
            resultsDiv.innerHTML = newResultsDiv.innerHTML;
          }

          const resultsDivMobile = document.querySelector("#results-mobile");
          const newResultsDivMobile = doc.querySelector("#results-mobile");

          if (resultsDivMobile && newResultsDivMobile) {
            resultsDivMobile.innerHTML = newResultsDivMobile.innerHTML;
          }

          // Update product count in collection header
          const productCount = document.querySelector(".collection-product-count");
          const newProductCount = doc.querySelector(".collection-product-count");

          if (productCount && newProductCount) {
            productCount.innerHTML = newProductCount.innerHTML;
          }

          resetAndPreload(doc);
        })
        .catch((error) => console.error("Error loading sorted products:", error));
    }
  };

  /**
   * Back button: keep the place in the list.
   *
   * When the visitor comes back through the back button and the browser does
   * not take the page from the back/forward cache, Shopify loads only the first
   * page. The loaded-in products were then gone and the visitor was back at the
   * top, even when they were 7,000 px deep. On leaving we therefore store how
   * many cards there were and where the page was scrolled to; on a back
   * navigation to exactly the same URL we load those cards again and scroll
   * back. From the back/forward cache everything is already there; then we do
   * nothing.
   */
  const RETURN_KEY = "gb-collection-return";
  const RETURN_MAX_AGE_MS = 30 * 60 * 1000;
  const returnUrl = () => window.location.pathname + window.location.search;

  window.addEventListener("pagehide", () => {
    // We restore the scroll position ourselves, AFTER loading more. Leave it to
    // the browser and it puts the visitor at the bottom of the still short
    // page; there the list then loads on to the end and they stay stuck at the
    // bottom. This has to happen before leaving: the mode belongs to this
    // history entry.
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";

    try {
      sessionStorage.setItem(
        RETURN_KEY,
        JSON.stringify({
          url: returnUrl(),
          cards: productGrid.children.length,
          scrollY: window.scrollY,
          at: Date.now(),
        }),
      );
    } catch {
      // No sessionStorage (private window, full): then without restoring.
    }
  });

  const readReturnState = () => {
    let saved = null;

    try {
      saved = JSON.parse(sessionStorage.getItem(RETURN_KEY) || "null");
      sessionStorage.removeItem(RETURN_KEY);
    } catch {
      return null;
    }

    if (!saved || saved.url !== returnUrl()) return null;

    if (Date.now() - saved.at > RETURN_MAX_AGE_MS) return null;

    const navigation =
      performance.getEntriesByType && performance.getEntriesByType("navigation")[0];

    if (!navigation || navigation.type !== "back_forward") return null;

    return saved;
  };

  const restoreReturnState = async (saved) => {
    bufferTarget = 2 * REVEAL_BATCH;
    const deadline = Date.now() + 15000;

    while (
      productGrid.children.length < saved.cards &&
      !(preloadComplete && preloadedProducts.length === 0)
    ) {
      if (Date.now() > deadline) break;

      // Wait for a page that is already on its way first: revealMoreProducts
      // starts no second fetch and would then add nothing.
      if (preloadedProducts.length === 0 && isPreloading) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        continue;
      }

      const before = productGrid.children.length;
      await revealMoreProducts();

      if (productGrid.children.length === before) break;
    }

    // Only scroll after the last cards are painted. In the same frame Chrome
    // counts the text block under the grid as shifted: it would have been in
    // view at the new scroll position had the cards not been added (measured:
    // CLS 0.77).
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    window.scrollTo(0, saved.scrollY);
  };

  // Initialize
  initRevealOnScroll();

  const returnState = readReturnState();

  if (returnState) {
    void restoreReturnState(returnState);
  } else {
    // Only fill the first buffer when the browser has nothing more urgent to
    // do. Starting right away cost over a second on the main thread, exactly in
    // the window in which the page has to become visible and clickable.
    const warmUpBuffer = () => topUpBuffer();

    if ("requestIdleCallback" in window) {
      requestIdleCallback(warmUpBuffer, { timeout: 3000 });
    } else {
      setTimeout(warmUpBuffer, 1500);
    }
  }

  // Event listeners

  // Quick filter pills and collection pills — single-select across both types
  const quickFilterPills = document.querySelectorAll(".js-quick-filter");
  const collectionPills = document.querySelectorAll(".js-collection-pill");
  const originalCollectionPath = window.location.pathname;

  /**
   * Deactivate all pills (both quick filters and collection pills)
   */
  const deactivateAllPills = () => {
    quickFilterPills.forEach((p) => p.classList.remove("is-active"));
    collectionPills.forEach((p) => p.classList.remove("is-active"));
    activeCollectionPill = null;
  };

  /**
   * Sets the checkboxes and fields in the filter drawer to match the URL.
   *
   * A pill replaces the URL, but the drawer was not updated. A filter the pill
   * had removed therefore stayed checked, and came back as soon as someone
   * clicked Toepassen (Apply) afterwards ("the filter does not switch off").
   * The live theme behaved like that too.
   */
  const syncFilterFormToUrl = () => {
    const params = new URLSearchParams(location.search);
    Array.from(filterForm.elements).forEach((element) => {
      if (!element.name) return;

      if (element.type === "checkbox" || element.type === "radio") {
        element.checked = params.getAll(element.name).includes(element.value);
      } else if (element.tagName === "INPUT" && element.type === "text") {
        const value = params.get(element.name) || "";

        if (element.value !== value) {
          element.value = value;
          // The price slider listens for input on these fields.
          element.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    });
  };

  /**
   * Path of the collection with only the current sorting. A pill left it out:
   * the label kept saying "Prijs: laag naar hoog" (price: low to high) while
   * the products were no longer in that order.
   */
  const collectionPathWithSort = (params = new URLSearchParams()) => {
    const sortBy = Shopify.queryParams.sort_by;

    if (sortBy) params.set("sort_by", sortBy);
    const query = params.toString();

    return query ? `${originalCollectionPath}?${query}` : originalCollectionPath;
  };

  /**
   * Fetch URL via AJAX and update product grid
   */
  const fetchAndUpdateGrid = (url) => {
    window.history.pushState({}, "", url);
    Shopify.queryParams = getQueryParams();
    syncFilterFormToUrl();

    httpGet(url)
      .then((response) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.data, "text/html");

        productGrid.innerHTML = doc.querySelector("#products-grid").innerHTML;
        activeerTrustpilot([productGrid]);

        const productCount = document.querySelector(".collection-product-count");
        const newProductCount = doc.querySelector(".collection-product-count");

        if (productCount && newProductCount) {
          productCount.innerHTML = newProductCount.innerHTML;
        }

        resetAndPreload(doc);

        // Scroll to top after filter is applied
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch((error) => console.error("Error applying filter:", error));
  };

  // Quick filter pills — metafield-based filters
  quickFilterPills.forEach((pill) => {
    pill.addEventListener("click", function (e) {
      e.preventDefault();
      const param = this.dataset.param;
      const value = this.dataset.value;
      const isActive = this.classList.contains("is-active");

      // Deactivate ALL pills (both types)
      deactivateAllPills();

      if (isActive) {
        // Deselecting — go back to original collection with no filters
        fetchAndUpdateGrid(collectionPathWithSort());
      } else {
        // Selecting — clean URL with only this filter on original collection
        this.classList.add("is-active");
        const params = new URLSearchParams();
        params.set(param, value);
        fetchAndUpdateGrid(collectionPathWithSort(params));
      }
    });
  });

  // Collection pills — intersection: show current collection products that also exist in target collection
  collectionPills.forEach((pill) => {
    pill.addEventListener("click", async function (e) {
      e.preventDefault();
      const targetHandle = this.dataset.collectionHandle;
      const isActive = this.classList.contains("is-active");

      // Deactivate ALL pills (both types)
      deactivateAllPills();

      if (isActive) {
        // Deselecting — go back to original collection
        activeCollectionPill = null;
        fetchAndUpdateGrid(collectionPathWithSort());
      } else {
        this.classList.add("is-active");
        activeCollectionPill = targetHandle;

        // The pill shows the intersection without filters from the drawer.
        // Set the URL and the drawer to match, otherwise those filters came
        // back silently on the next Toepassen (Apply). The sorting stays.
        const pillPath = collectionPathWithSort();
        window.history.pushState({}, "", pillPath);
        Shopify.queryParams = getQueryParams();
        syncFilterFormToUrl();

        // Stop infinite scroll while we work
        if (revealObserver) revealObserver.disconnect();
        preloadedProducts = [];
        preloadComplete = true;
        isPreloading = false;

        // Fetch target IDs using default-language handle (JSON API always uses default handles)
        const targetIds = await fetchCollectionProductIds(targetHandle);
        const matchingCards = await fetchFilteredCards(pillPath, targetIds);

        // Replace grid with only matching products
        productGrid.innerHTML = "";
        matchingCards.forEach((card) => productGrid.appendChild(card));
        activeerTrustpilot([productGrid]);

        // Update count
        const productCount = document.querySelector(".collection-product-count");

        if (productCount) {
          const count = matchingCards.length;
          const countNumbers = productCount.querySelectorAll(".js-product-count-number");

          if (countNumbers.length) {
            countNumbers.forEach((el) => (el.textContent = count));
          } else {
            productCount.textContent = count + (count === 1 ? " product" : " producten");
          }
        }

        if (endMessage) endMessage.classList.remove("d-none");

        if (loadingIndicator) loadingIndicator.classList.add("d-none");

        // Scroll to top after filter is applied
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  });

  // Filters are NOT applied on change — only when "Apply" button is clicked.
  //
  // The panel did not stay open until the products were there: it closed
  // right away and only afterwards, without a visible signal, was the grid
  // refreshed. On mobile 29% of the sessions that applied a filter applied it
  // twice or more (PostHog, 23-29 September 2026). Now the button shows that it
  // is busy, the panel only closes once the products are there, and a short
  // notice says how many products were found.
  const applyFilterBtns = document.querySelectorAll(".js-filter-apply");
  const applyError = document.querySelector(".js-filter-apply-error");
  const resultsToast = document.querySelector(".js-filter-results-toast");
  const resultsStatus = document.querySelector(".js-filter-results-status");
  let toastTimer = null;

  const setApplyBusy = (btn, busy) => {
    const label = btn.querySelector(".js-filter-apply-label");

    if (label) {
      if (!btn.dataset.labelIdle) btn.dataset.labelIdle = label.textContent;
      label.textContent = busy
        ? btn.dataset.labelBusy || btn.dataset.labelIdle
        : btn.dataset.labelIdle;
    }

    btn.setAttribute("aria-busy", busy ? "true" : "false");
  };

  const resultsText = (count) => {
    if (!resultsToast || count === null) return "";

    if (count === 0) return resultsToast.dataset.textNone || "";

    if (count === 1) return resultsToast.dataset.textOne || "";

    return (resultsToast.dataset.textOther || "").replace("__TOTAL__", count);
  };

  const showResultsCount = (count) => {
    const text = resultsText(count);

    if (!text) return;

    if (resultsStatus) resultsStatus.textContent = text;

    if (!resultsToast) return;

    resultsToast.textContent = text;
    resultsToast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => resultsToast.classList.remove("is-visible"), 3000);
  };

  // Closes the panel through toggles.js without simulating a click on the
  // close cross. That simulated click arrived in PostHog as a third click
  // within a second and so counted as a rage click nobody gave.
  const closeFilterDrawer = () => {
    document.dispatchEvent(new CustomEvent("gb:filter-drawer-close"));
  };

  applyFilterBtns.forEach((btn) => {
    btn.addEventListener("click", function (e) {
      e.preventDefault();

      if (btn.getAttribute("aria-busy") === "true") return;

      if (applyError) applyError.classList.add("d-none");
      setApplyBusy(btn, true);

      updateParamsAndFilter(true)
        .then((count) => {
          setApplyBusy(btn, false);
          closeFilterDrawer();
          // Scroll to top after filter is applied
          window.scrollTo({ top: 0, behavior: "smooth" });
          showResultsCount(count);
        })
        .catch((error) => {
          console.error("Error loading filtered products:", error);
          setApplyBusy(btn, false);

          // Leave the panel open: the choice is still there, so trying again
          // is one tap.
          if (applyError) applyError.classList.remove("d-none");
        });
    });
  });

  sortSelects.forEach((sortSelect) => {
    sortSelect.addEventListener("change", function (e) {
      const newValue = e.target.value;
      sortSelects.forEach((select) => {
        if (select !== e.target) {
          select.value = newValue;
        }
      });
      handleSortChange(newValue);
      // Scroll to top after sorting
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  });

  // Mobile filter bar sticky positioning — keep below header
  const mobileFilterBar = document.querySelector(".js-mobile-filter-bar");

  if (mobileFilterBar) {
    const updateMobileBarTop = () => {
      const header = document.querySelector(".header");
      const headerMain = header ? header.querySelector(".header-main") : null;

      if (header && header.classList.contains("is-sticky") && headerMain) {
        mobileFilterBar.style.top = headerMain.offsetHeight + "px";
      } else {
        mobileFilterBar.style.top = "0px";
      }

      // Toggle is-sticky class when bar is stuck
      const currentRect = mobileFilterBar.getBoundingClientRect();
      const stickyTop = parseFloat(mobileFilterBar.style.top) || 0;

      if (currentRect.top <= stickyTop + 1) {
        mobileFilterBar.classList.add("is-sticky");
      } else {
        mobileFilterBar.classList.remove("is-sticky");
      }
    };

    updateMobileBarTop();
    window.addEventListener("scroll", updateMobileBarTop, { passive: true });
    window.addEventListener("resize", updateMobileBarTop, { passive: true });
  }
});
